import React, { useCallback, useState, useRef } from 'react';
import { Upload, X, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { cn } from '../../lib/utils';
import { formatFileSize } from './fileConfig';

export const FileUploadZone = ({
  onUpload,
  categories = [],
  isUploading = false,
  className
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFiles(files);
    }
  }, []);

  const handleFiles = (files) => {
    // Add files to queue with default settings
    const newQueue = files.map(file => ({
      file,
      id: `${file.name}-${Date.now()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'pending', // pending, uploading, success, error
      progress: 0,
      categoryId: null,
      visibility: 'internal',
      tags: '',
      error: null
    }));
    
    setUploadQueue(prev => [...prev, ...newQueue]);
    
    // Auto-upload each file
    newQueue.forEach(item => {
      uploadFile(item);
    });
  };

  const uploadFile = async (queueItem) => {
    setUploadQueue(prev => 
      prev.map(item => 
        item.id === queueItem.id 
          ? { ...item, status: 'uploading', progress: 10 } 
          : item
      )
    );

    try {
      await onUpload(queueItem.file, {
        categoryId: queueItem.categoryId,
        visibility: queueItem.visibility,
        tags: queueItem.tags
      });
      
      setUploadQueue(prev => 
        prev.map(item => 
          item.id === queueItem.id 
            ? { ...item, status: 'success', progress: 100 } 
            : item
        )
      );
      
      // Remove successful uploads after delay
      setTimeout(() => {
        setUploadQueue(prev => prev.filter(item => item.id !== queueItem.id));
      }, 2000);
      
    } catch (error) {
      setUploadQueue(prev => 
        prev.map(item => 
          item.id === queueItem.id 
            ? { ...item, status: 'error', error: error.message || 'Upload failed' } 
            : item
        )
      );
    }
  };

  const removeFromQueue = (id) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFiles(files);
    }
    // Reset input
    e.target.value = '';
  };

  return (
    <div className={className}>
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all",
          isDragging 
            ? "border-primary bg-primary/5 scale-[1.02]" 
            : "border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50"
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        <Upload className={cn(
          "h-10 w-10 mx-auto mb-3 transition-colors",
          isDragging ? "text-primary" : "text-muted-foreground"
        )} />
        <p className="font-medium mb-1">
          {isDragging ? "Drop files here" : "Drag & drop files here"}
        </p>
        <p className="text-sm text-muted-foreground">
          or click to browse • Max 25MB per file
        </p>
      </div>

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <div className="mt-4 space-y-2">
          {uploadQueue.map(item => (
            <div
              key={item.id}
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg border",
                item.status === 'success' && "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800",
                item.status === 'error' && "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800",
                item.status === 'uploading' && "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800",
                item.status === 'pending' && "bg-muted"
              )}
            >
              <FileText className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(item.size)}</p>
                {item.status === 'uploading' && (
                  <Progress value={item.progress} className="h-1 mt-1" />
                )}
                {item.status === 'error' && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{item.error}</p>
                )}
              </div>
              <div className="flex-shrink-0">
                {item.status === 'uploading' && (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                )}
                {item.status === 'success' && (
                  <span className="text-xs text-green-600 font-medium">✓ Done</span>
                )}
                {(item.status === 'pending' || item.status === 'error') && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromQueue(item.id);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
